import { Group, MathUtils, Mesh, Object3D, Vector3 } from 'three';
import type { Material, PerspectiveCamera, Sphere } from 'three';
import { t } from '../../i18n/i18n';
import type { Fighter } from '../../rules/fighter';
import type { Hand, PunchStats } from '../../rules/types';
import { gloveRadius, playerFistInGlove } from '../bodies';
import { fleshMaterial } from '../boxer/flesh';
import { pushOff, pushOut, sweep } from '../collide';
import type { PunchingArm } from '../collide';
import { Landing } from '../landing';
import { buildGlove, gloveLength } from '../boxer/glove';
import { reach } from '../boxer/ik';
import { approach, punchReach } from '../boxer/motion';
import { limb } from '../boxer/shapes';
import { Spring } from '../boxer/spring';

const eyeHeight = 1.62;
/** The head's spring when a punch lands: nearly critically damped, so it snaps once and settles without bouncing. */
export const headJolt = { stiffness: 220, damping: 28 };
/** Radians of view turn per unit of the head's spring. */
const joltTurn = 0.4;
/** Where each glove waits in the guard, and covering up, in the eyes' space (the camera looks along -Z). */
const guard = { left: new Vector3(-0.24, -0.34, -0.4), right: new Vector3(0.25, -0.35, -0.36) };
const cover = { left: new Vector3(-0.2, -0.2, -0.34), right: new Vector3(0.2, -0.2, -0.34) };
/** The shoulders, just below and behind the eyes; a punch turns the shoulder forward. */
const shoulders = { left: new Vector3(-0.25, -0.42, 0.1), right: new Vector3(0.25, -0.44, 0.12) };
const upperArmLength = 0.36;
const foreArmLength = 0.36;
/** How far the forearm reaches into the glove's cuff. */
const cuffTuck = 0.04;

const aimAt = new Vector3();
const from = new Vector3();
const bend = new Vector3();
const shoulderNow = new Vector3();
const wrist = new Vector3();
const elbow = new Vector3();
const elbowPole = new Vector3();
const fist = new Vector3();
const fistWorld = new Vector3();
const homeAt = new Vector3();
const meets = new Vector3();

export interface OpponentTargets {
  /** The front of his face and of his middle, in the world: where the knuckles land. */
  readonly head: Vector3;
  readonly body: Vector3;
  /** His head, jaw, chest and belly as balls in the world: the player's gloves stop on them, never pass through. */
  readonly parts: readonly Sphere[];
  /**
   * His gloves as balls in the world. Covered up, he stops the player's punches on them; open, his guard gives way to
   * them, as the rules let them through.
   */
  readonly gloves: readonly Sphere[];
  /** His arm on its way in a punch, if any: the player's guard gives way to it unless he covers up. */
  readonly punching: readonly PunchingArm[];
  /** True while his gloves are up covering his face. */
  readonly covered: boolean;
}

interface Arm {
  readonly shoulder: Object3D;
  readonly upper: Object3D;
  readonly fore: Object3D;
  readonly glove: Group;
  readonly at: Vector3;
}

/**
 * The player's own view: the camera is his eyes, his thick forearms and blue gloves are all he sees of himself. Punches
 * fly along their own paths at the opponent's face or body, in time with the rules, the body stepping and turning into
 * them; the gloves come up to cover, the view ducks in a dodge, snaps back when hit and drops at a knockout.
 */
export class FirstPerson {
  readonly body = new Object3D();
  private readonly arms: Record<Hand, Arm>;
  private readonly jolt = new Spring(0, headJolt.stiffness, headJolt.damping);
  private readonly roll = new Spring(0, headJolt.stiffness, headJolt.damping);
  private coverAmount = 0;
  /** What the player's guard, and his punches, rest on. */
  private readonly guardParts: Sphere[] = [];
  private readonly punchParts: Sphere[] = [];
  /** Where each fist was placed last frame, and this frame: a punch travels from one to the next. */
  private readonly fistsBefore = { left: new Vector3(), right: new Vector3() };
  private readonly fists = { left: new Vector3(), right: new Vector3() };
  private placed = false;
  /** Where each hand's punch really got to. */
  private readonly landings = { left: new Landing(), right: new Landing() };
  /** How worn out he is: it follows his stamina over a moment, as a punch's cost lands all at once. */
  private tiredness = 0;
  /** How far through a breath: it runs faster when he is tired, never jumping. */
  private breathPhase = 0;

  /** How far the gloves are up in front of the face: 0 in the guard, 1 covering up. */
  get covered(): number {
    return this.coverAmount;
  }
  private dip = 0;
  private fall = -1;
  private watching = false;
  private backing = 0;
  private time = 0;
  private shake = new Vector3();

  constructor(private readonly camera: PerspectiveCamera, standAt: Vector3) {
    this.body.position.copy(standAt);
    this.body.add(camera);
    camera.position.set(0, eyeHeight, 0);
    camera.rotation.set(0, 0, 0);
    const skin = fleshMaterial(0xb06c48);
    this.arms = {
      left: this.arm(skin, 'left'),
      right: this.arm(skin, 'right'),
    };
  }

  // Shoulder, upper arm and forearm, pointed at the glove every frame; the glove carries on along the forearm.
  private arm(skin: Material, hand: Hand): Arm {
    const shoulder = new Object3D();
    shoulder.position.copy(shoulders[hand]);
    const upper = new Object3D();
    const upperMesh = new Mesh(limb(0.092, 0.088, 24), skin);
    upperMesh.scale.set(1, 1, upperArmLength);
    upper.add(upperMesh);
    const fore = new Object3D();
    fore.position.z = upperArmLength;
    const foreMesh = new Mesh(limb(0.09, 0.082, 24), skin);
    foreMesh.scale.set(1, 1, foreArmLength);
    fore.add(foreMesh);
    upper.add(fore);
    shoulder.add(upper);
    const glove = buildGlove({ leather: 0x1f4fd6, trim: 0x1c2d70, label: t('glove-label') }, hand === 'left' ? 1 : -1);
    this.camera.add(shoulder, glove);
    return { shoulder, upper, fore, glove, at: new Vector3() };
  }

  /** A clean hit on the player: the view snaps up from a punch to the face, down from one to the body, and tilts
   * away from the hand that threw it; less through a block. */
  hit(punch: PunchStats, blocked: boolean): void {
    const strength = blocked ? 0.35 : punch.staggers ? 1.4 : 1;
    this.jolt.kick((punch.target === 'body' ? -6 : 6) * strength);
    this.roll.kick((punch.hand === 'left' ? 1 : -1) * 4 * strength);
  }

  /** Extra turn of the view this frame, in degrees, from the camera shake. */
  setShake(degrees: Vector3): void {
    this.shake.copy(degrees);
  }

  knockedOut(): void {
    this.fall = 0;
  }

  /** He knocked the opponent out: a step back, and a look down at him on the canvas. */
  watchFall(): void {
    this.watching = true;
  }

  reset(): void {
    this.fall = -1;
    this.watching = false;
    this.backing = 0;
    this.coverAmount = 0;
    this.dip = 0;
    this.placed = false;
  }

  update(seconds: number, me: Fighter, opponent: OpponentTargets): void {
    this.time += seconds;
    const t = this.time;
    const punch = me.currentPunch;
    const extension = punch ? punchReach(punch) : 0;
    const stats = punch?.stats;
    this.coverAmount = approach(this.coverAmount, me.state === 'blocking' ? 1 : 0, 26, seconds);
    this.dip = approach(this.dip, me.state === 'dodging' && me.isDodging ? 1 : 0, 20, seconds);
    this.tiredness = approach(this.tiredness, 1 - me.staminaFraction, 4, seconds);
    const tired = this.tiredness;

    // The body steps and turns into a punch; ducks in a dodge; breathes, harder when tired.
    const out = Math.max(0, extension);
    const turn = stats ? (stats.hand === 'left' ? 1 : -1) * (stats.action === 'straight' ? 0.05 : 0.09) * out : 0;
    this.breathPhase += seconds * (2 + tired * 3);
    const breath = Math.sin(this.breathPhase) * (0.006 + tired * 0.012);
    this.backing = approach(this.backing, this.watching ? 1 : 0, 1.6, seconds);
    const back = MathUtils.smoothstep(this.backing, 0, 1);
    this.camera.position.set(this.dip * 0.14, eyeHeight - this.dip * 0.26 + breath, -out * 0.1 + back * 0.35);
    let pitch = this.jolt.update(0, seconds) * joltTurn + this.shake.x * MathUtils.DEG2RAD - back * 0.32;
    let yaw = turn + this.shake.y * MathUtils.DEG2RAD;
    let rollAngle = this.roll.update(0, seconds) * joltTurn + this.dip * 0.12 + this.shake.z * MathUtils.DEG2RAD;
    if (this.fall >= 0) {
      this.fall += seconds;
      const f = MathUtils.smoothstep(this.fall, 0, 1.3);
      this.camera.position.y -= f * 1.2;
      pitch += f * 0.9;
      rollAngle += f * 0.35;
    }
    this.camera.rotation.set(pitch, yaw, rollAngle, 'YXZ');
    this.camera.updateMatrixWorld();

    // Whoever covers up stops the other's gloves on his own; whoever does not makes way for them.
    this.guardParts.length = this.punchParts.length = 0;
    this.guardParts.push(...opponent.parts);
    this.punchParts.push(...opponent.parts, ...(opponent.covered ? opponent.gloves : []));
    for (const hand of ['left', 'right'] as const) {
      const arm = this.arms[hand];
      const home = homeAt.copy(guard[hand]).lerp(cover[hand], this.coverAmount);
      home.y -= tired * 0.05;
      home.x += Math.sin(t * 2.2 + (hand === 'left' ? 0 : 1)) * 0.006;
      home.y += Math.sin(t * 3 + (hand === 'left' ? 0.5 : 0)) * 0.008;
      const mine = stats?.hand === hand;
      if (punch && mine && extension !== 0) {
        this.landings[hand].follow(punch);
        this.punchPath(arm, hand, punch.stats, extension, opponent, home, this.landings[hand].holds);
      } else arm.at.copy(home);
      const punching = mine && out > 0;
      this.fistsBefore[hand].copy(this.fists[hand]);
      this.place(arm, hand, mine ? stats : undefined, mine ? out : 0, punching ? this.punchParts : this.guardParts, punching || this.coverAmount > 0.5 ? [] : opponent.punching);
      if (punching) this.landings[hand].reach(arm.at);
    }
    this.placed = true;
  }

  // From the guard to a spot just in front of what it hits, bent out for a hook, up from below for an uppercut.
  // Once it stopped on something, and on its way back, from where the punch really got to.
  private punchPath(arm: Arm, hand: Hand, stats: PunchStats, extension: number, opponent: OpponentTargets, home: Vector3, fromLanding: boolean): void {
    aimAt.copy(stats.target === 'body' ? opponent.body : opponent.head);
    this.camera.worldToLocal(aimAt);
    // The knuckles stop on him, so the wrist stops a glove's length short of him along the arm; a hook comes in from
    // the side, an uppercut from under the chin.
    const side = hand === 'left' ? -1 : 1;
    if (isHook(stats)) aimAt.add(from.set(side * 0.22, 0.02, -0.06));
    if (stats.action === 'uppercut') aimAt.add(from.set(0, -0.28, -0.08));
    const along = from.copy(aimAt).sub(this.shoulderAt(hand, 1)).normalize();
    aimAt.addScaledVector(along, -gloveLength);
    if (fromLanding) aimAt.copy(this.landings[hand].at);
    bend.set(0, 0, 0);
    if (isHook(stats)) bend.set(side * 0.32, 0.08, 0.05);
    if (stats.action === 'uppercut') bend.set(side * 0.05, -0.28, 0.02);
    const arc = Math.sin(Math.max(0, extension) * Math.PI);
    arm.at.copy(home).lerp(aimAt, extension).addScaledVector(bend, arc);
  }

  private place(arm: Arm, hand: Hand, stats: PunchStats | undefined, out: number, parts: readonly Sphere[], giveWay: readonly PunchingArm[]): void {
    const side = hand === 'left' ? 1 : -1;
    arm.shoulder.position.copy(this.shoulderAt(hand, out));
    // The glove stops where the arm reaches: the rules decide the hit, not the picture.
    const toGlove = from.copy(arm.at).sub(arm.shoulder.position);
    const most = upperArmLength + foreArmLength - 0.01;
    if (toGlove.length() > most) arm.at.copy(arm.shoulder.position).addScaledVector(toGlove.normalize(), most);
    // The elbow hangs down and out behind the guard, tucks in under the gloves to cover, and swings out level for a hook.
    const hook = stats && isHook(stats) ? out : 0;
    elbowPole.set(-side * 0.6, -1, 0.4).lerp(from.set(0, -1, 0.1), this.coverAmount).lerp(from.set(-side, 0.2, 0), hook).transformDirection(this.camera.matrixWorld);
    for (let pass = 0; pass < 3; pass++) {
      // Where the fist ends up in his head or body, or a glove it must not pass, move the wrist by as much and place
      // the arm again.
      this.put(arm, elbowPole);
      // A punch stops where it first meets him on its way from the last frame, as his do: put anywhere else, it
      // would snap back or swing off, frame to frame. The wrist goes wherever puts the fist there.
      if (out > 0 && pass === 0 && this.placed) {
        const share = sweep(this.fistsBefore[hand], fistWorld, gloveRadius, parts);
        if (share < 1) {
          this.camera.worldToLocal(meets.lerpVectors(this.fistsBefore[hand], fistWorld, share));
          for (let aim = 0; aim < 3; aim++) {
            arm.at.add(meets).sub(fist);
            this.put(arm, elbowPole);
          }
          this.landings[hand].stop(arm.at);
          continue;
        }
      }
      const pushed = pushOut(fistWorld, gloveRadius, parts);
      if (!(pushOff(fistWorld, gloveRadius, giveWay) || pushed)) break;
      arm.at.add(this.camera.worldToLocal(fistWorld).sub(fist));
    }
    this.fists[hand].copy(fistWorld);
  }

  // The arm reaching for its glove where it is now, the glove on the forearm, and where that puts the fist.
  private put(arm: Arm, pole: Vector3): void {
    reach(arm.upper, arm.fore, upperArmLength, foreArmLength, this.camera.localToWorld(wrist.copy(arm.at)), pole);
    // The glove carries on along the forearm, its back up, the wrist tucked into the cuff.
    arm.glove.position.copy(arm.at);
    arm.glove.lookAt(arm.fore.getWorldPosition(elbow));
    arm.glove.translateZ(-cuffTuck);
    arm.glove.updateWorldMatrix(true, false);
    arm.glove.localToWorld(fistWorld.copy(playerFistInGlove));
    this.camera.worldToLocal(fist.copy(fistWorld));
  }

  /** The player's gloves, left and right, each where its wrist is. */
  get gloveJoints(): readonly Object3D[] {
    return [this.arms.left.glove, this.arms.right.glove];
  }

  /** Everything of the player's own arms, for hiding while the camera is away from his eyes. */
  get armParts(): Object3D[] {
    return [this.arms.left.shoulder, this.arms.left.glove, this.arms.right.shoulder, this.arms.right.glove];
  }

  /** Where the middle of one of the player's padded fists is, in the world. */
  fist(hand: Hand, out: Vector3): Vector3 {
    return this.arms[hand].glove.localToWorld(out.copy(playerFistInGlove));
  }

  /** The player's head and both fists as balls in the world, for his gloves to stop on. */
  placeBalls(head: Sphere, fists: readonly Sphere[]): void {
    this.camera.getWorldPosition(head.center);
    head.radius = 0.14;
    (['left', 'right'] as const).forEach((hand, i) => {
      this.arms[hand].glove.localToWorld(fists[i].center.copy(playerFistInGlove));
      fists[i].radius = gloveRadius;
    });
  }

  // The shoulder turns forward into a punch, which is how a real one reaches.
  private shoulderAt(hand: Hand, out: number): Vector3 {
    const side = hand === 'left' ? 1 : -1;
    return shoulderNow.copy(shoulders[hand]).add(elbow.set(-side * 0.04 * out, 0.06 * out, -0.26 * out));
  }
}

function isHook(stats: PunchStats): boolean {
  return stats.action === 'hook' || stats.action === 'super';
}
